import { defineStore } from 'pinia';
import { simpleDebugLog } from '@/utils/simpleDebugLogger';

//  UNIVERSAL ITEMS
import channelsData from '@/data/channels.json';
import contactsData from '@/data/contacts.json';
import contactCentersData from '@/data/contactCenters.json';
import groupMessagesData from '@/data/groupMessages.json';
import linksData from '@/data/links.json';
import userCallsData from '@/data/userCalls.json';
// users.json has been merged into contacts.json with is_system_user flag

//  SHARED CONVERSATIONS
import groupMessagesConversationsData from '@/data/groupMessagesConversations.json';
import sharedChannelMessagesData from '@/data/sharedChannelMessages.json';

//  USER SPECIFIC ITEMS
import userLinksData from '@/data/userLinks.json';
import userSidebarGroupsData from '@/data/userSidebarGroups.json';
import userContactsData from '@/data/userContacts.json';
import userContactsMessagesData from '@/data/userContactsMessages.json';
import userCentersData from '@/data/userCenters.json';
import userChannelsData from '@/data/userChannels.json';
import userChannelMessagesData from '@/data/userChannelMessages.json';
import userGroupMessagesData from '@/data/userGroupMessages.json';

// Type definitions
export interface Contact {
  id: string;
  name: string;
  is_system_user?: boolean;
  title?: string;
  avatar_url?: string;
  avatar_color?: string;
  phone?: string;
  phone_label?: string;
  phone_display?: string;
  email?: string;
  time_zone?: string;
  last_active?: string;
  pronouns?: string;
  location?: string;
  status?: string;
  status_message?: string;
  avatar_present?: boolean;
  url_id?: string;
  slug?: string;
  reports_to?: string;
  persona_notes?: string | string[];
  persona_profile?: any;
  user_id?: string;
  userId?: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  presence?: {
    state?: string;
    status?: string;
  };
  [key: string]: any; // Allow for additional properties
}

export interface UserDataItem {
  contact_id?: string;
  contact_url_id?: string;
  group_message_id?: string;
  group_message_url_id?: string;
  channel_id?: string;
  channel_url_id?: string;
  center_id?: string;
  center_url_id?: string;
  user_sidebar_group_id?: string | null;
  user_sidebar_group_url_id?: string;
  unread_count?: number;
  // Add other properties as needed
}

export type ItemType = 'contact' | 'direct' | 'group_message' | 'channel' | 'contact_center';

export interface DataStoreState {
  channelsData: any;
  contactsData: { contacts: Contact[] };
  contactCentersData: any;
  groupMessagesData: any;
  linksData: any;
  userCallsData: any;
  groupMessagesConversationsData: any;
  sharedChannelMessagesData: any;
  userLinksData: any;
  userSidebarGroupsData: any;
  userContactsData: Record<string, UserDataItem[]>;
  userContactsMessagesData: any;
  userCentersData: Record<string, UserDataItem[]>;
  userChannelsData: Record<string, UserDataItem[]>;
  userChannelMessagesData: any;
  userGroupMessagesData: Record<string, UserDataItem[]>;
  readMessages: string[]; // Track explicitly marked as read message IDs
  unreadMessages: string[]; // Track explicitly marked as unread message IDs
}

export const useDataStore = defineStore('dataStore', {
  state: (): DataStoreState => ({
    channelsData,
    contactsData,
    contactCentersData,
    groupMessagesData,
    linksData,
    userCallsData,
    groupMessagesConversationsData,
    sharedChannelMessagesData,
    userLinksData,
    userSidebarGroupsData,
    userContactsData,
    userContactsMessagesData,
    userCentersData,
    userChannelsData,
    userChannelMessagesData,
    userGroupMessagesData,
    readMessages: [], // Track explicitly marked as read message IDs
    unreadMessages: [], // Track explicitly marked as unread message IDs
  }),

  getters: {
    // Get system users from contacts (replaces the old users.json)
    usersData: state => {
      return {
        users: state.contactsData.contacts.filter(contact => contact.is_system_user),
      };
    },

    // Get conversation by ID (for direct messages/contacts)
    getConversationById: state => {
      return (conversationId: string) => {
        return state.contactsData.contacts.find((contact: Contact) => contact.id === conversationId) || null;
      };
    },

    // Get channel by ID
    getChannelById: state => {
      return (channelId: string) => {
        return state.channelsData?.channels?.find((channel: any) => channel.id === channelId) || null;
      };
    },

    // Get all recent messages across all conversation types
    recentMessages: state => {
      const currentUserId = '09f2780b-6785-4b16-b94a-7605eb5cc1ba'; // TODO: Get from auth
      const allMessages: any[] = [];

      // Helper function to determine unread status
      const getUnreadStatus = (message: any, type: string, threshold: number = 0.5): boolean => {
        // For messages, use a simple heuristic:
        // - Messages from others to current user are more likely to be unread
        // - Messages from current user are usually read (since they sent them)
        const isFromCurrentUser = (message.senderId || message.sender) === currentUserId;

        if (isFromCurrentUser) {
          return false; // User's own messages are read
        } else {
          // Messages from others - equal weighting for all types
          // Use message content to create consistent unread status
          const textLength = message.text ? message.text.length : 0;
          const hash = textLength % 3; // Use mod 3 for consistent ~33% unread rate
          return hash === 0; // About 1/3 of messages from others are unread
        }
      };

      // Process each conversation type
      const processConversations = (conversations: any[], type: string, threshold: number) => {
        if (!conversations) return;

        conversations.forEach((conversation: any) => {
          if (!conversation.messages) return;

          // Find the most recent message in this conversation
          const messages = conversation.messages;
          messages.forEach((message: any) => {
            allMessages.push({
              ...message,
              type,
              conversationId: conversation.contact_id || conversation.link_id || conversation.group_id,
              unread: getUnreadStatus(message, type, threshold), // Deterministic unread status
            });
          });
        });
      };

      // Get contact messages
      processConversations(state.userContactsMessagesData[currentUserId], 'contact', 0.7);

      // Get channel messages
      processConversations(state.userChannelMessagesData[currentUserId], 'channel', 0.8);

      // Get group messages (from shared conversations)
      Object.entries(state.groupMessagesConversationsData).forEach(([groupId, messages]: [string, any]) => {
        if (Array.isArray(messages)) {
          messages.forEach((message: any) => {
            allMessages.push({
              ...message,
              type: 'group',
              conversationId: groupId,
              unread: getUnreadStatus(message, 'group', 0.7),
            });
          });
        }
      });

      // Get call messages (include them naturally in chronological order)
      const userCalls = state.userCallsData?.calls || [];
      userCalls.forEach((call: any) => {
        // Only include calls involving the current user
        if (call.caller_id === currentUserId || call.receiver_id === currentUserId) {
          // Determine the other participant
          const otherParticipantId = call.caller_id === currentUserId ? call.receiver_id : call.caller_id;

          // Create a message representation of the call
          const callMessage = {
            id: call.id,
            senderId: call.caller_id,
            timestamp: call.timestamp,
            text: `${call.direction === 'outgoing' ? 'Outgoing' : 'Incoming'} call - ${call.duration}`,
            type: 'call',
            conversationId: otherParticipantId,
            call_data: {
              duration: call.duration,
              direction: call.direction,
              call_type: call.type,
              summary: call.summary,
            },
            // Only missed calls should be unread
            unread:
              call.status === 'missed' || call.duration === '0 seconds' || call.duration === '0 minutes 0 seconds',
          };

          allMessages.push(callMessage);
        }
      });

      // Sort by timestamp (most recent first) and take the top 200 messages
      // Increased from 100 to ensure we capture unread messages
      return allMessages
        .sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 200)
        .map((message: any, index: number) => {
          // Create a stable ID based on message content and type
          const messageId = `${message.type}_${message.conversationId}_${message.timestamp}_${index}`;
          return {
            ...message,
            id: messageId,
            unread: state.readMessages.includes(messageId)
              ? false
              : state.unreadMessages.includes(messageId)
                ? true
                : message.unread,
          };
        });
    },

    // Get the unread status of a message, considering explicit read/unread actions
    getMessageUnreadStatus: state => {
      return (messageId: string, defaultUnread: boolean) => {
        // If explicitly marked as read, return false
        if (state.readMessages.includes(messageId)) {
          return false;
        }
        // If explicitly marked as unread, return true
        if (state.unreadMessages.includes(messageId)) {
          return true;
        }
        // Otherwise, use the default unread status
        return defaultUnread;
      };
    },
  },

  actions: {
    // Clear unread count for an item when it becomes active
    clearUnreadCount(itemId: string, itemType: ItemType, userId: string) {
      if (!userId) return;

      // Determine which collection to update based on item type
      let userDataCollection: Record<string, UserDataItem[]>;
      let itemIdField: keyof UserDataItem;

      switch (itemType) {
        case 'contact':
          userDataCollection = this.userContactsData;
          itemIdField = 'contact_id';
          break;
        case 'direct':
          userDataCollection = this.userContactsData;
          itemIdField = 'contact_id';
          break;
        case 'group_message':
          userDataCollection = this.userGroupMessagesData;
          itemIdField = 'group_message_id';
          break;
        case 'channel':
          userDataCollection = this.userChannelsData;
          itemIdField = 'channel_id';
          break;
        case 'contact_center':
          userDataCollection = this.userCentersData;
          itemIdField = 'center_id';
          break;
        default:
          simpleDebugLog.error('Unknown item type:', itemType);
          return;
      }

      // Get the user's data array
      const userData = userDataCollection[userId];
      if (!userData) return;

      // Find the item in the user's data
      const dataItemIndex = userData.findIndex(item => item[itemIdField] === itemId);
      if (dataItemIndex === -1) return;

      // Update the unread count to zero
      userData[dataItemIndex].unread_count = 0;
    },

    // Mark a message as read
    markMessageAsRead(messageId: string) {
      // Add to read messages if not already there
      if (!this.readMessages.includes(messageId)) {
        this.readMessages.push(messageId);
      }
      // Remove from unread messages if it was marked as unread
      const unreadIndex = this.unreadMessages.indexOf(messageId);
      if (unreadIndex > -1) {
        this.unreadMessages.splice(unreadIndex, 1);
      }
    },

    // Mark a message as unread
    markMessageAsUnread(messageId: string) {
      // Remove from read messages if it was marked as read
      const readIndex = this.readMessages.indexOf(messageId);
      if (readIndex > -1) {
        this.readMessages.splice(readIndex, 1);
      }
      // Add to unread messages if not already there
      if (!this.unreadMessages.includes(messageId)) {
        this.unreadMessages.push(messageId);
      }
    },
  },

  persist: {
    key: 'dp3-data-store',
    // Only persist the conversation data that can be modified
    pick: [
      'groupMessagesConversationsData',
      'userContactsMessagesData',
      'userChannelMessagesData',
      'userGroupMessagesData',
      'userSidebarGroupsData',
      'readMessages',
      'unreadMessages',
    ],
  },
});
